import { z } from 'zod';

import { apiClient } from './client';
import { uuidv4 } from '@/utils/uuid';

// Auth API module — phone+OTP login, profile, logout.
// Port of source/mobile/src/api/auth.ts (same Zod schemas, same endpoints).

export const LocaleZ = z.enum(['ru', 'kk']);
export const TaxRegimeZ = z.enum([
  'self_employed',
  'ip_simplified',
  'ip_general',
  'ip_patent',
  'too_simplified',
  'too_general',
]);

export const UserZ = z.object({
  id: z.number(),
  phone: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  iin_bin: z.string().nullable(),
  // Роли-флага больше нет: бэкенд выводит способности из данных.
  is_owner: z.boolean(),
  is_tenant: z.boolean(),
  tax_regime: TaxRegimeZ.nullable(),
  locale: LocaleZ,
  avatar_path: z.string().nullable(),
  bank_account: z.string().nullable().optional(),
  bank_bik: z.string().nullable().optional(),
  bank_name: z.string().nullable().optional(),
});

export type User = z.infer<typeof UserZ>;
export type Locale = z.infer<typeof LocaleZ>;
export type TaxRegime = z.infer<typeof TaxRegimeZ>;

const SendCodeResponseZ = z.object({ message: z.string() });

const VerifyResponseZ = z.object({
  token: z.string(),
  user: UserZ,
  is_new: z.boolean(),
});

// Backend wraps the profile in `user` (see AuthController::me / updateProfile),
// NOT `data`. Mobile's auth.ts uses `user` too -- the web port had it wrong,
// which made me() throw and dropped the session on every page refresh.
const MeResponseZ = z.object({ user: UserZ });
const UpdateProfileResponseZ = z.object({ user: UserZ });

export const AuthErrorEnvelopeZ = z.object({
  message: z.string(),
  error_code: z
    .enum(['otp_send_throttled', 'otp_too_many_attempts', 'otp_invalid_code', 'otp_expired'])
    .optional(),
});

export type AuthErrorCode = NonNullable<z.infer<typeof AuthErrorEnvelopeZ>['error_code']>;

export interface UpdateProfilePayload {
  name?: string;
  email?: string | null;
  tax_regime?: TaxRegime | null;
  iin_bin?: string | null;
  locale?: Locale;
  avatar_path?: string | null;
  bank_account?: string | null;
  bank_bik?: string | null;
  bank_name?: string | null;
}

export async function sendCode(phone: string): Promise<{ message: string }> {
  const res = await apiClient.post('/auth/send-code', { phone });
  return SendCodeResponseZ.parse(res.data);
}

export async function verify(
  phone: string,
  code: string,
): Promise<{ token: string; user: User; is_new: boolean }> {
  const res = await apiClient.post(
    '/auth/verify',
    { phone, code },
    { headers: { 'Idempotency-Key': uuidv4() } },
  );
  return VerifyResponseZ.parse(res.data);
}

export async function me(): Promise<User> {
  const res = await apiClient.get('/auth/me');
  return MeResponseZ.parse(res.data).user;
}

export async function updateProfile(payload: UpdateProfilePayload): Promise<User> {
  const res = await apiClient.put('/auth/profile', payload);
  return UpdateProfileResponseZ.parse(res.data).user;
}

export async function logout(): Promise<void> {
  await apiClient.delete('/auth/logout');
}

export function parseAuthError(
  err: unknown,
): { code: AuthErrorCode | undefined; message: string } | null {
  const maybeData = (err as { response?: { data?: unknown } } | undefined)?.response?.data;
  if (!maybeData) return null;
  const parsed = AuthErrorEnvelopeZ.safeParse(maybeData);
  if (!parsed.success) return null;
  return { code: parsed.data.error_code, message: parsed.data.message };
}

export const authApi = {
  sendCode,
  verify,
  me,
  updateProfile,
  logout,
  parseAuthError,
};
